import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver-es';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Delegation } from '../comun/recipient-services/delegation';
import { Expedition, codigosProductosDDI } from '../comun/recipient-services/expedition';
import { ExpeditionModification } from '../comun/recipient-services/expedition-modification';
import { ModificationResult } from '../comun/recipient-services/modification-result';
import { PackageInfo } from '../comun/recipient-services/package-info';
import { TransitEvent } from '../comun/recipient-services/transit-event';
import { ChangeType } from '../enums/change-type.enum';
import { ApiService } from './api.service';

@Injectable()
export class ExpeditionsService extends ApiService {

  constructor() {
    super('shipment-detail');
  }

  search(number: string, zipCode?: string, parameters?: {
    [key: string]: any;
  }): Observable<Expedition> {
    const options = {
      params: new HttpParams()
        .set('number', number)
    };
    if (zipCode) {
      options.params = options.params.set('zipCode', zipCode);
    }
    if (parameters) {
      for (const name in parameters) {
        if (parameters.hasOwnProperty(name)) {
          options.params = options.params.set(name, parameters[name]);
        }
      }
    }
    return this.http.get<Expedition>(this.url(''), options);
  }

  searchByReference(reference: string, parameters?: {
    [key: string]: any;
  }): Observable<any> {
    const options = {
      params: new HttpParams()
        .set('reference', reference)
    };
    if (parameters) {
      for (const name in parameters) {
        if (parameters.hasOwnProperty(name)) {
          options.params = options.params.set(name, parameters[name]);
        }
      }
    }
    return this.http.get<Expedition>(this.url('package-by-reference'), options);
  }

  searchById(id: string): Observable<Expedition> {
    return this.http.get<Expedition>(this.url(''), {
      params: new HttpParams()
        .set('token', id)
    }).pipe(
      map(expedition => {
        return expedition;
      })
    );
  }

  typesChangeAvailable(typeSelected: ChangeType): Observable<ChangeType> {
    return this.http.get<ChangeType>(this.url('change-types'), {
      params: new HttpParams()
        .set('selectedType', typeSelected)
    });
  }

  modify(modification: ExpeditionModification): Observable<ModificationResult> {
    return this.http.post<ModificationResult>(this.url('modify-shipment'), modification);
  }

  detailPackages(numberExpeditionTLG: string): Observable<Array<PackageInfo>> {
    return this.http.get<Array<PackageInfo>>(this.url('packages'), {
      params: new HttpParams()
        .set('number', numberExpeditionTLG)
    });
  }

  tracking(licensePlate: string): Observable<Array<TransitEvent>> {
    return this.http.get<Array<TransitEvent>>(this.url('package-tracking'), {
      params: new HttpParams()
        .set('licensePlate', licensePlate)
    });
  }

  delegation(numberExpeditionTLG: string): Observable<Delegation> {
    return this.http.get<Delegation>(this.url('delegation'), {
      params: new HttpParams()
        .set('numberExpeditionTLG', numberExpeditionTLG)
    });
  }

  downloadVoucher(expedition: Expedition, zipCode: string): Observable<void> {
    const ddi = codigosProductosDDI.indexOf(expedition.ProductCode) != -1
    const number = ddi ? expedition.InternationalNumber.trim() : expedition.TLGNumber.substring(0, 2) + expedition.TLGNumber.substring(5);

    return new Observable<void>(suscriptor => {
      this.http.get(this.url('pod'), {
        params: new HttpParams()
          .set('number', expedition.TLGNumber)
          .set('zipCode', zipCode),
        responseType: 'blob'
      }).subscribe(pdf => {
        saveAs(pdf, number + '.pdf')
        suscriptor.next();
        suscriptor.complete();
      }, error => {
        suscriptor.error(error);
        suscriptor.complete();
      });
    });

  }

  validateZipCode(numberExpeditionTLG: string, zipCode: string): Observable<boolean> {
    return this.http.get<boolean>(this.url('validate-zipcode'), {
      params: new HttpParams()
        .set('tlgNumber', numberExpeditionTLG)
        .set('zipCode', zipCode)
    });
  }
}
